import React, { useState, useEffect } from 'react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Calendar, Cloud, Droplet, Thermometer, Sun, Moon, TrendingUp, AlertTriangle, CheckCircle, Clock, MapPin, Leaf, Zap, Award, ChevronLeft, ChevronRight, Sprout, Scissors } from 'lucide-react';
import 'chart.js/auto';
import './HarvestingCycle.css';

const FarmerDashboard = () => {
  const [selectedCrop, setSelectedCrop] = useState('Wheat');
  const [selectedState, setSelectedState] = useState('Punjab');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('overview');
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [sowingDate, setSowingDate] = useState(new Date('2024-11-15')); // Default sowing date for wheat
  const [weatherData, setWeatherData] = useState({
    temperature: 24,
    humidity: 65,
    rainfall: 12,
    windSpeed: 15,
    forecast: [
      { day: 'Today', temp: 24, icon: Sun, desc: 'Sunny' },
      { day: 'Tomorrow', temp: 22, icon: Cloud, desc: 'Cloudy' },
      { day: 'Day 3', temp: 19, icon: Droplet, desc: 'Rainy' },
      { day: 'Day 4', temp: 26, icon: Sun, desc: 'Clear' },
    ]
  });
  const [loadingWeather, setLoadingWeather] = useState(false);

  const crops = ['Wheat', 'Rice', 'Maize', 'Sugarcane', 'Cotton', 'Barley', 'Soybean', 'Mustard'];
  
  const allIndianStates = [
    'Punjab', 'Haryana', 'Uttar Pradesh', 'Madhya Pradesh', 'Rajasthan', 'Maharashtra', 
    'Karnataka', 'Gujarat', 'Andhra Pradesh', 'Tamil Nadu', 'West Bengal', 'Bihar',
    'Odisha', 'Telangana', 'Kerala', 'Assam', 'Jharkhand', 'Chhattisgarh'
  ];

  const cropInfo = {
    Wheat: {
      properties: 'Cool season crop, requires 100-125 days to mature. Optimal temp: 15-22°C',
      fertilizer: 'N: 120 kg/ha, P: 60 kg/ha, K: 40 kg/ha',
      sowingPeriod: 'November - December',
      harvestPeriod: 'March - April',
      waterRequirement: '450-650 mm',
      soilType: 'Well-drained loamy soil',
      diseases: ['Rust', 'Smut', 'Blight'],
      productivity: '3.2 tons/ha',
      marketPrice: '₹2,100/quintal',
      cropCycle: 120, // days
      defaultSowingMonth: 10, // November (0-indexed)
      farmingSchedule: [
        { task: 'Land Preparation', dayFromSowing: -15, duration: 7, type: 'preparation', priority: 'high' },
        { task: 'Sowing', dayFromSowing: 0, duration: 3, type: 'sowing', priority: 'high' },
        { task: 'First Irrigation', dayFromSowing: 20, duration: 1, type: 'irrigation', priority: 'high' },
        { task: 'First Fertilizer Application', dayFromSowing: 25, duration: 2, type: 'fertilizer', priority: 'high' },
        { task: 'Weed Management', dayFromSowing: 30, duration: 3, type: 'weed', priority: 'medium' },
        { task: 'Second Irrigation', dayFromSowing: 45, duration: 1, type: 'irrigation', priority: 'high' },
        { task: 'Pest Monitoring', dayFromSowing: 50, duration: 2, type: 'pest', priority: 'medium' },
        { task: 'Second Fertilizer Application', dayFromSowing: 55, duration: 2, type: 'fertilizer', priority: 'medium' },
        { task: 'Third Irrigation', dayFromSowing: 70, duration: 1, type: 'irrigation', priority: 'high' },
        { task: 'Disease Monitoring', dayFromSowing: 80, duration: 3, type: 'disease', priority: 'high' },
        { task: 'Fourth Irrigation', dayFromSowing: 95, duration: 1, type: 'irrigation', priority: 'medium' },
        { task: 'Pre-harvest Inspection', dayFromSowing: 110, duration: 2, type: 'inspection', priority: 'high' },
        { task: 'Harvesting', dayFromSowing: 120, duration: 7, type: 'harvest', priority: 'high' }
      ]
    },
    Rice: {
      properties: 'Warm season crop, requires flooded conditions. Growth period: 120-150 days',
      fertilizer: 'N: 100 kg/ha, P: 50 kg/ha, K: 50 kg/ha',
      sowingPeriod: 'June - July',
      harvestPeriod: 'October - November',
      waterRequirement: '1200-1500 mm',
      soilType: 'Clay or clay loam',
      diseases: ['Blast', 'Sheath blight', 'Brown spot'],
      productivity: '4.1 tons/ha',
      marketPrice: '₹1,940/quintal',
      cropCycle: 135, // days
      defaultSowingMonth: 5, // June (0-indexed)
      farmingSchedule: [
        { task: 'Field Preparation & Puddling', dayFromSowing: -10, duration: 5, type: 'preparation', priority: 'high' },
        { task: 'Transplanting/Sowing', dayFromSowing: 0, duration: 4, type: 'sowing', priority: 'high' },
        { task: 'Maintain Water Level', dayFromSowing: 7, duration: 120, type: 'irrigation', priority: 'high' },
        { task: 'First Fertilizer (Basal)', dayFromSowing: 15, duration: 2, type: 'fertilizer', priority: 'high' },
        { task: 'Weed Control', dayFromSowing: 25, duration: 3, type: 'weed', priority: 'medium' },
        { task: 'Second Fertilizer (Top dressing)', dayFromSowing: 40, duration: 2, type: 'fertilizer', priority: 'high' },
        { task: 'Pest Management', dayFromSowing: 60, duration: 5, type: 'pest', priority: 'high' },
        { task: 'Third Fertilizer Application', dayFromSowing: 70, duration: 2, type: 'fertilizer', priority: 'medium' },
        { task: 'Disease Monitoring', dayFromSowing: 85, duration: 10, type: 'disease', priority: 'high' },
        { task: 'Drain Field (Pre-harvest)', dayFromSowing: 120, duration: 3, type: 'preparation', priority: 'high' },
        { task: 'Harvesting', dayFromSowing: 135, duration: 8, type: 'harvest', priority: 'high' }
      ]
    },
    Maize: {
      properties: 'Versatile crop, grows in diverse conditions. Maturity: 90-120 days',
      fertilizer: 'N: 150 kg/ha, P: 75 kg/ha, K: 60 kg/ha',
      sowingPeriod: 'June - July',
      harvestPeriod: 'September - October',
      waterRequirement: '500-800 mm',
      soilType: 'Well-drained fertile soil',
      diseases: ['Downy mildew', 'Leaf blight', 'Stalk rot'],
      productivity: '2.9 tons/ha',
      marketPrice: '₹1,850/quintal',
      cropCycle: 100, // days
      defaultSowingMonth: 5, // June (0-indexed)
      farmingSchedule: [
        { task: 'Land Preparation', dayFromSowing: -8, duration: 5, type: 'preparation', priority: 'high' },
        { task: 'Sowing', dayFromSowing: 0, duration: 2, type: 'sowing', priority: 'high' },
        { task: 'First Irrigation', dayFromSowing: 15, duration: 1, type: 'irrigation', priority: 'high' },
        { task: 'First Fertilizer (Basal)', dayFromSowing: 20, duration: 2, type: 'fertilizer', priority: 'high' },
        { task: 'Weed Management', dayFromSowing: 25, duration: 3, type: 'weed', priority: 'medium' },
        { task: 'Second Irrigation', dayFromSowing: 35, duration: 1, type: 'irrigation', priority: 'high' },
        { task: 'Second Fertilizer (Side dressing)', dayFromSowing: 45, duration: 2, type: 'fertilizer', priority: 'high' },
        { task: 'Third Irrigation', dayFromSowing: 55, duration: 1, type: 'irrigation', priority: 'medium' },
        { task: 'Pest Control', dayFromSowing: 60, duration: 3, type: 'pest', priority: 'high' },
        { task: 'Fourth Irrigation', dayFromSowing: 70, duration: 1, type: 'irrigation', priority: 'medium' },
        { task: 'Disease Monitoring', dayFromSowing: 80, duration: 5, type: 'disease', priority: 'medium' },
        { task: 'Pre-harvest Check', dayFromSowing: 95, duration: 2, type: 'inspection', priority: 'high' },
        { task: 'Harvesting', dayFromSowing: 100, duration: 5, type: 'harvest', priority: 'high' }
      ]
    }
  };

  const ndviData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5', 'Week 6', 'Week 7', 'Week 8'],
    datasets: [
      {
        label: 'NDVI Index',
        data: [0.2, 0.35, 0.48, 0.62, 0.71, 0.68, 0.65, 0.58],
        fill: true,
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4,
        pointBackgroundColor: '#10b981',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };

  const yieldPredictionData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Predicted Yield (tons/ha)',
        data: [3.2, 3.4, 3.6, 3.8, 4.0, 4.2],
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: '#3b82f6',
        borderWidth: 2,
      },
    ],
  };

  const cropDistributionData = {
    labels: ['Wheat', 'Rice', 'Maize', 'Others'],
    datasets: [
      {
        data: [40, 30, 20, 10],
        backgroundColor: ['#f59e0b', '#10b981', '#8b5cf6', '#ef4444'],
        borderWidth: 0,
      },
    ],
  };

  // Function to fetch weather data from backend
  const fetchWeatherData = async (city) => {
    if (loadingWeather) return;
    
    setLoadingWeather(true);
    try {
      // Fetch current weather
      const currentResponse = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/india/weather/dynamic?q=${city}`);
      const currentData = await currentResponse.json();
      
      // Fetch forecast
      const forecastResponse = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/india/forecast?q=${city}&days=5`);
      const forecastData = await forecastResponse.json();
      
      // Transform forecast data to match our format
      const transformedForecast = forecastData.forecast.forecastday.map((day, index) => {
        const date = new Date(day.date);
        const dayNames = ['Today', 'Tomorrow', 'Day 3', 'Day 4', 'Day 5'];
        const dayName = index < dayNames.length ? dayNames[index] : date.toLocaleDateString('en-US', { weekday: 'short' });
        
        // Determine icon based on condition
        let IconComponent = Sun;
        const condition = day.day.condition.text.toLowerCase();
        if (condition.includes('cloud')) IconComponent = Cloud;
        if (condition.includes('rain') || condition.includes('shower')) IconComponent = Droplet;
        
        return {
          day: dayName,
          temp: Math.round(day.day.avgtemp_c),
          icon: IconComponent,
          desc: day.day.condition.text
        };
      });
      
      // Update state with fetched data
      setWeatherData({
        temperature: Math.round(currentData.current.temp_c),
        humidity: currentData.current.humidity,
        rainfall: currentData.current.precip_mm,
        windSpeed: Math.round(currentData.current.wind_kph),
        forecast: transformedForecast
      });
    } catch (error) {
      console.error('Error fetching weather data:', error);
      // Keep using default data in case of error
    } finally {
      setLoadingWeather(false);
    }
  };

  // Fetch weather data when component mounts or selected state changes
  useEffect(() => {
    fetchWeatherData(selectedState);
  }, [selectedState]);

  const tasks = [
    { id: 1, task: 'Apply nitrogen fertilizer', date: '2025-08-05', priority: 'high', completed: false, type: 'fertilizer' },
    { id: 2, task: 'Check irrigation system', date: '2025-08-03', priority: 'medium', completed: true, type: 'irrigation' },
    { id: 3, task: 'Pest monitoring', date: '2025-08-07', priority: 'high', completed: false, type: 'pest' },
    { id: 4, task: 'Soil pH testing', date: '2025-08-10', priority: 'low', completed: false, type: 'soil' },
  ];

  const achievements = [
    { title: 'Water Saver', icon: Droplet, desc: 'Reduced water usage by 15%' },
    { title: 'Yield Master', icon: TrendingUp, desc: 'Achieved 20% above average yield' },
    { title: 'Eco Farmer', icon: Leaf, desc: 'Used organic fertilizers for 6 months' },
  ];

  // Calculate farming schedule based on sowing date
  const calculateFarmingSchedule = () => {
    const schedule = [];
    const crop = cropInfo[selectedCrop];
    
    crop.farmingSchedule.forEach(task => {
      const taskDate = new Date(sowingDate);
      taskDate.setDate(taskDate.getDate() + task.dayFromSowing);
      
      schedule.push({
        ...task,
        date: taskDate,
        dateString: taskDate.toISOString().split('T')[0],
        formattedDate: taskDate.toLocaleDateString('en-IN', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        })
      });
    });
    
    return schedule.sort((a, b) => a.date - b.date);
  };

  const farmingSchedule = calculateFarmingSchedule();

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getTasksForDate = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return farmingSchedule.filter(task => {
      const taskDate = new Date(task.date);
      const endDate = new Date(taskDate);
      endDate.setDate(endDate.getDate() + task.duration - 1);
      
      return date >= taskDate && date <= endDate;
    });
  };

  const getTaskTypeColor = (type) => {
    const colors = {
      preparation: '#8b5cf6',
      sowing: '#10b981',
      irrigation: '#3b82f6',
      fertilizer: '#f59e0b',
      weed: '#ef4444',
      pest: '#f97316',
      disease: '#dc2626',
      inspection: '#6366f1',
      harvest: '#059669'
    };
    return colors[type] || '#6b7280';
  };

  // Update sowing date when crop changes
  useEffect(() => {
    const crop = cropInfo[selectedCrop];
    const newDate = new Date();
    newDate.setMonth(crop.defaultSowingMonth);
    newDate.setDate(15);
    setSowingDate(newDate);
    setCalendarDate(newDate);
  }, [selectedCrop]);

  const TabButton = ({ id, label, icon: Icon, active, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`tabButton ${active ? 'tabButtonActive' : 'tabButtonInactive'}`}
      onMouseEnter={(e) => {
        if (!active) {
          e.target.classList.add('tabButtonHover');
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.target.classList.remove('tabButtonHover');
        }
      }}
    >
      <Icon size={20} />
      <span>{label}</span>
    </button>
  );

  const WeatherCard = ({ icon: Icon, label, value, unit, colorClass }) => (
    <div 
      className="weatherCard"
      onMouseEnter={(e) => e.target.classList.add('weatherCardHover')}
      onMouseLeave={(e) => e.target.classList.remove('weatherCardHover')}
    >
      <div>
        <p className="weatherLabel">{label}</p>
        <p className="weatherValue">{value}<span className="weatherUnit">{unit}</span></p>
      </div>
      <div className={`weatherIcon weatherIcon${colorClass}`}>
        <Icon size={24} />
      </div>
    </div>
  );

  const TaskCard = ({ task, onToggle }) => (
    <div 
      className={`taskCard taskCard${task.priority.charAt(0).toUpperCase() + task.priority.slice(1)} ${task.completed ? 'taskCardCompleted' : ''}`}
      onMouseEnter={(e) => e.target.classList.add('taskCardHover')}
      onMouseLeave={(e) => e.target.classList.remove('taskCardHover')}
    >
      <div className="taskContent">
        <button
          onClick={() => onToggle(task.id)}
          className={`taskCheckbox ${task.completed ? 'taskCheckboxCompleted' : ''}`}
          onMouseEnter={(e) => {
            if (!task.completed) e.target.classList.add('taskCheckboxHover');
          }}
          onMouseLeave={(e) => {
            if (!task.completed) e.target.classList.remove('taskCheckboxHover');
          }}
        >
          {task.completed && <CheckCircle color="white" size={16} />}
        </button>
        <div>
          <h4 className={`taskTitle ${task.completed ? 'taskTitleCompleted' : ''}`}>
            {task.task}
          </h4>
          <p className="taskDate">
            <Clock size={14} className="taskDateIcon" />
            {task.date}
          </p>
        </div>
      </div>
      <span className={`taskPriority taskPriority${task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}`}>
        {task.priority}
      </span>
    </div>
  );

  // Calendar Component
  const CalendarComponent = () => {
    const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const navigateMonth = (direction) => {
      const newDate = new Date(calendarDate);
      newDate.setMonth(newDate.getMonth() + direction);
      setCalendarDate(newDate);
    };

    const renderCalendar = () => {
      const daysInMonth = getDaysInMonth(calendarDate);
      const firstDay = getFirstDayOfMonth(calendarDate);
      const daysInPrevMonth = getDaysInMonth(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1));
      
      const days = [];

      // Previous month days
      for (let i = firstDay - 1; i >= 0; i--) {
        const day = daysInPrevMonth - i;
        const date = new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, day);
        const tasks = getTasksForDate(date);
        
        days.push(
          <div key={`prev-${day}`} className="calendarDay">
            <div className="calendarDayNumber calendarDayOther">{day}</div>
            {tasks.map((task, idx) => (
              <div
                key={idx}
                className="calendarTask calendarTaskOther"
                style={{ backgroundColor: getTaskTypeColor(task.type) }}
                title={task.task}
              >
                {task.task}
              </div>
            ))}
          </div>
        );
      }

      // Current month days
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(calendarDate.getFullYear(), calendarDate.getMonth(), day);
        const tasks = getTasksForDate(date);
        const isToday = date.toDateString() === new Date().toDateString();
        const isSowingDate = date.toDateString() === sowingDate.toDateString();
        
        days.push(
          <div 
            key={day} 
            className={`calendarDay ${isToday ? 'calendarDayToday' : ''} ${isSowingDate ? 'calendarDaySowing' : ''}`}
          >
            <div className={`calendarDayNumber ${isToday ? 'calendarDayNumberToday' : ''} ${isSowingDate ? 'calendarDayNumberSowing' : ''}`}>
              {day}
              {isSowingDate && <Sprout size={12} className="calendarDaySowingIcon" />}
            </div>
            {tasks.map((task, idx) => (
              <div
                key={idx}
                className="calendarTask"
                style={{ backgroundColor: getTaskTypeColor(task.type) }}
                title={`${task.task} - ${task.formattedDate}`}
              >
                {task.task}
              </div>
            ))}
          </div>
        );
      }

      // Next month days
      const totalCells = 42; // 6 rows × 7 days
      const remainingCells = totalCells - days.length;
      
      for (let day = 1; day <= remainingCells; day++) {
        const date = new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, day);
        const tasks = getTasksForDate(date);
        
        days.push(
          <div key={`next-${day}`} className="calendarDay">
            <div className="calendarDayNumber calendarDayOther">{day}</div>
            {tasks.map((task, idx) => (
              <div
                key={idx}
                className="calendarTask calendarTaskOther"
                style={{ backgroundColor: getTaskTypeColor(task.type) }}
                title={task.task}
              >
                {task.task}
              </div>
            ))}
          </div>
        );
      }

      return days;
    };

    return (
      <div className="calendarContainer">
        <div className="calendarHeader">
          <button
            className="calendarNavButton"
            onClick={() => navigateMonth(-1)}
          >
            <ChevronLeft size={20} />
          </button>
          <h3 className="calendarTitle">
            {monthNames[calendarDate.getMonth()]} {calendarDate.getFullYear()}
          </h3>
          <button
            className="calendarNavButton"
            onClick={() => navigateMonth(1)}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="calendarGrid">
          {daysOfWeek.map(day => (
            <div key={day} className="calendarDayHeader">
              {day}
            </div>
          ))}
          {renderCalendar()}
        </div>

        <div className="sowingDateContainer">
          <label className="sowingDateLabel">
            <Sprout size={16} className="sowingDateIcon" />
            Sowing Date for {selectedCrop}
          </label>
          <input
            type="date"
            value={sowingDate.toISOString().split('T')[0]}
            onChange={(e) => setSowingDate(new Date(e.target.value))}
            className="sowingDateInput"
          />
        </div>
      </div>
    );
  };

  // Farming Schedule Component
  const FarmingScheduleComponent = () => {
    const getScheduleIcon = (type) => {
      const icons = {
        preparation: Leaf,
        sowing: Sprout,
        irrigation: Droplet,
        fertilizer: Zap,
        weed: AlertTriangle,
        pest: AlertTriangle,
        disease: AlertTriangle,
        inspection: CheckCircle,
        harvest: Scissors
      };
      return icons[type] || CheckCircle;
    };

    return (
      <div className="calendarContainer">
        <h3 className="cardHeader farmingScheduleHeader">
          <Clock className="cardHeaderIcon" size={24} />
          Farming Schedule for {selectedCrop}
        </h3>
        
        <div className="scheduleList">
          {farmingSchedule.map((item, index) => {
            const IconComponent = getScheduleIcon(item.type);
            const isCompleted = item.date < new Date();
            const isUpcoming = item.date >= new Date() && item.date <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
            
            return (
              <div 
                key={index} 
                className={`scheduleItem ${isCompleted ? 'scheduleItemCompleted' : ''} ${isUpcoming ? 'scheduleItemUpcoming' : ''}`}
              >
                <div className="scheduleItemContent">
                  <div className="scheduleIcon" style={{ backgroundColor: getTaskTypeColor(item.type) }}>
                    <IconComponent size={16} />
                  </div>
                  <div className="scheduleText">
                    <p className={`scheduleTitle ${isCompleted ? 'scheduleTitleCompleted' : ''}`}>{item.task}</p>
                    <p className="scheduleDate">{item.formattedDate}</p>
                  </div>
                </div>
                <span className={`scheduleBadge taskPriority${item.priority.charAt(0).toUpperCase() + item.priority.slice(1)}`}>
                  {item.priority}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="dashboardContainer">
      {/* Header */}
      <div className="header">
        <div className="headerContent">
          <div>
            <h1 className="headerTitle">
              🌾 AI Farmer Support
              <Zap className="headerIcon" size={32} />
            </h1>
            <p className="headerSubtitle">Monitor, analyze, and optimize your agricultural operations</p>
          </div> 
        </div>
      </div>

      <div className="mainContent">
        {/* Controls */}
        <div className="controlsSection">
          <div className="controlsGrid">
            <div>
              <label className="controlLabel">
                <MapPin size={20} className="controlIcon" />
                Select State
              </label>
              <select 
                value={selectedState} 
                onChange={(e) => setSelectedState(e.target.value)}
                className="controlSelect"
                onFocus={(e) => {
                  e.target.classList.add('controlSelectFocus');
                }}
                onBlur={(e) => {
                  e.target.classList.remove('controlSelectFocus');
                }}
              >
                {allIndianStates.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="controlLabel">
                <Leaf size={20} className="controlIcon" />
                Select Crop
              </label>
              <select 
                value={selectedCrop} 
                onChange={(e) => setSelectedCrop(e.target.value)}
                className="controlSelect"
                onFocus={(e) => {
                  e.target.classList.add('controlSelectFocus');
                }}
                onBlur={(e) => {
                  e.target.classList.remove('controlSelectFocus');
                }}
              >
                {crops.map((crop) => (
                  <option key={crop} value={crop}>{crop}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="tabNavigation">
          <TabButton id="overview" label="Overview" icon={Sun} active={activeTab === 'overview'} onClick={setActiveTab} />
          <TabButton id="analytics" label="Analytics" icon={TrendingUp} active={activeTab === 'analytics'} onClick={setActiveTab} />
          <TabButton id="tasks" label="Tasks" icon={CheckCircle} active={activeTab === 'tasks'} onClick={setActiveTab} />
          <TabButton id="weather" label="Weather" icon={Cloud} active={activeTab === 'weather'} onClick={setActiveTab} />
          <TabButton id="calendar" label="Calendar" icon={Calendar} active={activeTab === 'calendar'} onClick={setActiveTab} />
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Quick Stats */}
            <div className="statsGrid">
              <div className="statCard statCardBlue">
                <div>
                  <p className="statLabel">Current Yield</p>
                  <p className="statValue">{cropInfo[selectedCrop]?.productivity}</p>
                </div>
                <TrendingUp size={32} className="statIcon" />
              </div>
              <div className="statCard statCardGreen">
                <div>
                  <p className="statLabel">Market Price</p>
                  <p className="statValue">{cropInfo[selectedCrop]?.marketPrice}</p>
                </div>
                <Award size={32} className="statIcon" />
              </div>
              <div className="statCard statCardPurple">
                <div>
                  <p className="statLabel">Water Usage</p>
                  <p className="statValue">{cropInfo[selectedCrop]?.waterRequirement}</p>
                </div>
                <Droplet size={32} className="statIcon" />
              </div>
              <div className="statCard statCardOrange">
                <div>
                  <p className="statLabel">Soil Health</p>
                  <p className="statValue">Good</p>
                </div>
                <Leaf size={32} className="statIcon" />
              </div>
            </div>

            {/* Crop Information */}
            <div className="card">
              <h2 className="cardHeader">
                <Leaf className="cardHeaderIcon" size={28} />
                {selectedCrop} Information
              </h2>
              <div className="cropInfoGrid">
                <div className="infoSection">
                  <div className="infoItem infoItemGreen">
                    <h4 className="infoTitle infoTitleGreen">Properties</h4>
                    <p className="infoContent">{cropInfo[selectedCrop]?.properties}</p>
                  </div>
                  <div className="infoItem infoItemBlue">
                    <h4 className="infoTitle infoTitleBlue">Fertilizer Requirements</h4>
                    <p className="infoContent">{cropInfo[selectedCrop]?.fertilizer}</p>
                  </div>
                  <div className="infoItem infoItemPurple">
                    <h4 className="infoTitle infoTitlePurple">Soil Type</h4>
                    <p className="infoContent">{cropInfo[selectedCrop]?.soilType}</p>
                  </div>
                </div>
                <div className="infoSection">
                  <div className="infoItem infoItemYellow">
                    <h4 className="infoTitle infoTitleYellow">Sowing Period</h4>
                    <p className="infoContent">{cropInfo[selectedCrop]?.sowingPeriod}</p>
                  </div>
                  <div className="infoItem infoItemOrange">
                    <h4 className="infoTitle infoTitleOrange">Harvest Period</h4>
                    <p className="infoContent">{cropInfo[selectedCrop]?.harvestPeriod}</p>
                  </div>
                  <div className="infoItem infoItemRed">
                    <h4 className="infoTitle infoTitleRed">Common Diseases</h4>
                    <p className="infoContent">{cropInfo[selectedCrop]?.diseases?.join(', ')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* NDVI Chart */}
            <div className="card">
              <h2 className="cardHeader">
                <TrendingUp className="cardHeaderIcon" size={28} />
                Crop Health Monitoring (NDVI)
              </h2>
              <Line data={ndviData} options={{
                responsive: true,
                plugins: {
                  legend: { display: false },
                },
                scales: {
                  y: { beginAtZero: true, max: 1 }
                }
              }} />
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            <div className="analyticsGrid">
              <div className="card">
                <h2 className="cardHeader">Yield Prediction</h2>
                <Bar data={yieldPredictionData} options={{ responsive: true }} />
              </div>
              <div className="card">
                <h2 className="cardHeader">Crop Distribution</h2>
                <Doughnut data={cropDistributionData} options={{ responsive: true }} />
              </div>
            </div>
            
            {/* Achievements */}
            <div className="card">
              <h2 className="cardHeader">
                <Award className="cardHeaderIcon" size={28} />
                Your Achievements
              </h2>
              <div className="achievementsGrid">
                {achievements.map((achievement, index) => (
                  <div key={index} className="achievementCard">
                    <div className="achievementHeader">
                      <achievement.icon className="achievementIcon" size={24} />
                      <h3 className="achievementTitle">{achievement.title}</h3>
                    </div>
                    <p className="achievementDesc">{achievement.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="card">
            <h2 className="cardHeader">
              <CheckCircle className="cardHeaderIcon" size={28} />
              Farm Tasks
            </h2>
            <div>
              {tasks.map((task) => (
                <TaskCard key={task.id} task={task} onToggle={() => {}} />
              ))}
            </div>
          </div>
        )}

        {/* Weather Tab */}
        {activeTab === 'weather' && (
          <div>
            <div className="statsGrid">
              <WeatherCard icon={Thermometer} label="Temperature" value={weatherData.temperature} unit="°C" colorClass="Red" />
              <WeatherCard icon={Droplet} label="Humidity" value={weatherData.humidity} unit="%" colorClass="Blue" />
              <WeatherCard icon={Cloud} label="Rainfall" value={weatherData.rainfall} unit="mm" colorClass="Indigo" />
              <WeatherCard icon={Sun} label="Wind Speed" value={weatherData.windSpeed} unit="km/h" colorClass="Yellow" />
            </div>
            
            <div className="card">
              <h2 className="cardHeader">5-Day Forecast</h2>
              <div className="forecastGrid">
                {weatherData.forecast.map((day, index) => (
                  <div key={index} className="forecastCard">
                    <p className="forecastDay">{day.day}</p>
                    <day.icon className="forecastIcon" size={32} />
                    <p className="forecastTemp">{day.temp}°C</p>
                    <p className="forecastDesc">{day.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="analyticsGrid">
            <CalendarComponent />
            <FarmingScheduleComponent />
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmerDashboard;